import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import {
  compact,
  endsWith,
  filter,
  includes,
  join,
  keyBy,
  map,
  mapValues,
  size,
  some,
  split,
  toLower,
  trim,
} from "lodash";
import { EComModal } from "components/EComModal";
import { message } from "components";
import DebounceSearchInput from "components/DebounceSearchInput";
import { AiccTestAccount } from "types/model/aiccTestAccount";
import * as aiccTestAccountAPI from "services/aiccTestAccount";
import styles from "./TestAccount.module.scss";

const allowedEmailSuffixes = (): string[] =>
  compact(map(split(process.env.REACT_APP_TEST_ACCOUNT_EMAIL_SUFFIXES, ","), (suffix) => trim(suffix)));

const emailSuffixHint = () => join(allowedEmailSuffixes(), ", ");

const isAllowedTestAccountEmail = (email: string) => {
  const suffixes = allowedEmailSuffixes();
  if (!size(suffixes)) {
    return true;
  }
  return some(suffixes, (suffix) => endsWith(trim(email), suffix));
};

interface Props {
  visible: boolean;
  addedEmails: string[];
  onCancel: () => void;
  onAdded: (accounts: AiccTestAccount[]) => void;
}

function AddTestAccountModal({ visible, addedEmails, onCancel, onAdded }: Props) {
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<string>();
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const addedEmailsRef = useRef(addedEmails);
  addedEmailsRef.current = map(addedEmails, (email) => toLower(trim(email)));

  /** The add API takes a customer_id, while the dropdown shows and selects emails. */
  const customerIdByEmailRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setSelectedEmail(undefined);
      setIsSearching(false);
      setHasSearched(false);
    }
  }, [form, visible]);

  const fetchOptions = useCallback((keyword: string) => {
    const email = trim(keyword);
    if (!email) {
      setIsSearching(false);
      setHasSearched(false);
      return Promise.resolve([]);
    }
    setIsSearching(true);
    setHasSearched(true);
    return aiccTestAccountAPI
      .searchAiccTestAccountCandidates(email, allowedEmailSuffixes())
      .then((candidates) => {
        const available = filter(
          candidates,
          ({ customerEmail }) =>
            isAllowedTestAccountEmail(customerEmail) && !includes(addedEmailsRef.current, toLower(trim(customerEmail)))
        );
        customerIdByEmailRef.current = mapValues(keyBy(available, "customerEmail"), "customerId");
        return map(available, ({ customerId, customerEmail }) => ({
          key: customerId,
          label: customerEmail,
          value: customerEmail,
        }));
      })
      .finally(() => setIsSearching(false));
  }, []);

  const handleOk = () => {
    form.validateFields().then(({ email }) => {
      const customerId = customerIdByEmailRef.current[email];
      if (!customerId) {
        message.error(new Error("Please select an email."));
        return;
      }
      setIsSaving(true);
      aiccTestAccountAPI
        .addAiccTestAccount(customerId)
        .then((accounts) => {
          message.success(`Added ${email} successfully.`);
          onAdded(accounts);
          onCancel();
        })
        .finally(() => setIsSaving(false));
    });
  };

  const renderNotFoundContent = () => {
    if (isSearching) {
      return (
        <div className={styles.emptyState}>
          <Spin size="small" />
        </div>
      );
    }
    if (!hasSearched) {
      return null;
    }
    return (
      <div className={styles.emptyState}>
        No customer available. The email must end with {emailSuffixHint()} and must not be added yet.
      </div>
    );
  };

  return (
    <EComModal
      visible={visible}
      title="Add Test Account"
      okText="Confirm"
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={isSaving}
      okButtonProps={{ shape: "round", style: { minWidth: "92px" }, disabled: !selectedEmail }}
    >
      <div className={styles.suffixHint}>
        <div>Allowed email suffixes:</div>
        <div>{emailSuffixHint()}</div>
      </div>
      <Form form={form} layout="vertical" onValuesChange={(_, { email }) => setSelectedEmail(email)}>
        <Form.Item label="Email" name="email" rules={[{ required: true, message: "Please select an email." }]}>
          <DebounceSearchInput
            showSearch
            allowClear
            placeholder="Search by email"
            suffixIcon={<SearchOutlined />}
            fetchOptions={fetchOptions}
            notFoundContent={renderNotFoundContent()}
          />
        </Form.Item>
      </Form>
    </EComModal>
  );
}

export default AddTestAccountModal;
